import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle, ArrowLeft, Copy } from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";

export default function Reserve() {
  const { id } = useParams();
  const { waLink, settings } = useSettings();
  const [product, setProduct] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(false));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/reservations", { product_id: id, first_name: firstName, phone });
      setConfirmation(r.data);
      toast.success("Réservation enregistrée !");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (product === false)
    return (
      <div className="container-oxy py-20 text-center">
        <h1 className="font-display text-2xl font-bold">Produit introuvable</h1>
        <Link to="/catalogue" className="btn-ink mt-6">Retour au catalogue</Link>
      </div>
    );

  if (confirmation) {
    const hasWhatsApp = Boolean((settings?.whatsapp_number || "").trim());
    return (
      <div className="container-oxy max-w-xl py-12" data-testid="reservation-confirmation">
        <div className="card-oxy p-6 text-center sm:p-10">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
            <CheckCircle2 className="h-9 w-9 text-emerald-600" />
          </span>
          <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Réservation confirmée</h1>
          <p className="mt-2 text-sm text-slate-600">Présentez cette référence en boutique pour retirer votre produit.</p>
          <div className="mt-6 rounded-2xl border-2 border-dashed border-gold bg-gold-soft p-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-gold-dark">Votre référence</p>
            <p className="mt-1 font-mono text-3xl font-bold tracking-wider text-ink" data-testid="reservation-reference">{confirmation.reference}</p>
            <button
              data-testid="copy-reference-btn"
              onClick={() => { navigator.clipboard?.writeText(confirmation.reference); toast.success("Référence copiée"); }}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-gold-dark hover:underline">
              <Copy className="h-3.5 w-3.5" /> Copier
            </button>
          </div>
          <p className="mt-5 text-sm text-slate-600">
            <strong>{confirmation.product_name}</strong> — {confirmation.price} €<br />
            Paiement et retrait en boutique.
          </p>
          {hasWhatsApp && (
            <a
              href={waLink(`Bonjour Oxyservices, je confirme ma réservation ${confirmation.reference} : ${confirmation.product_name}.`)}
              target="_blank" rel="noopener noreferrer" data-testid="reservation-whatsapp-confirm"
              className="btn-gold mt-6 w-full">
              <MessageCircle className="h-4 w-4" /> Confirmer sur WhatsApp
            </a>
          )}
          <Link to="/catalogue" className="btn-outline-oxy mt-3 w-full">Retour au catalogue</Link>
        </div>
      </div>
    );
  }

  if (!product) return <div className="container-oxy py-16"><div className="mx-auto aspect-[3/2] max-w-xl animate-pulse rounded-3xl bg-slate-100" /></div>;

  return (
    <div className="container-oxy max-w-xl py-10" data-testid="reserve-page">
      <Link to={`/produit/${id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-gold-dark">
        <ArrowLeft className="h-4 w-4" /> Retour au produit
      </Link>
      <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-900">Réserver ce produit</h1>
      <p className="mt-2 text-sm text-slate-600">Gratuit et sans paiement en ligne. Vous réglez en boutique au retrait.</p>

      <div className="card-oxy mt-6 flex items-center gap-4 p-4" data-testid="reserve-product-summary">
        {product.images?.[0] && (
          <img src={fileUrl(product.images[0])} alt="" className="h-20 w-20 rounded-xl object-cover" />
        )}
        <div>
          <p className="font-display font-bold text-slate-900">{product.name}</p>
          <p className="mt-0.5 font-display text-xl font-bold text-gold-dark">{product.price} €</p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 space-y-5">
        <div>
          <label className="label-oxy" htmlFor="reserve-firstname">Prénom</label>
          <input id="reserve-firstname" data-testid="reserve-firstname-input" required maxLength={80}
            value={firstName} onChange={(e) => setFirstName(e.target.value)}
            placeholder="Votre prénom" className="input-oxy" />
        </div>
        <div>
          <label className="label-oxy" htmlFor="reserve-phone">Téléphone</label>
          <input id="reserve-phone" data-testid="reserve-phone-input" required type="tel" maxLength={20}
            value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="06 12 34 56 78" className="input-oxy" />
        </div>
        <button type="submit" data-testid="reserve-submit-btn" disabled={loading}
          className="btn-gold w-full disabled:opacity-60">
          {loading ? "Envoi…" : "Confirmer la réservation"}
        </button>
      </form>
    </div>
  );
}
